import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Search,
  ClipboardCheck,
  FileText,
  Wrench,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";

// Geotagged images
import heroImage from "@/assets/waterproofing-basement-repair-ridgewood-nj-t.webp";
import basementParamus from "@/assets/basement-waterproofing-paramus-nj.webp";
import basementWallington from "@/assets/basement-waterproofing-wallington-bergen-county-nj.webp";
import basementInspection from "@/assets/basement-waterproofing-inspection-paramus-nj.webp";

const waterproofingSteps = [
  {
    number: "1",
    title: "Inspect",
    description: "Inspect the basement for cracks, seepage, and moisture on walls and floors.",
  },
  {
    number: "2",
    title: "Check Exterior",
    description: "Check exterior grading and downspouts. Soil should slope away from the foundation.",
  },
  {
    number: "3",
    title: "Choose Method",
    description: "Interior drainage and sump systems handle active water. Exterior membranes prevent water from reaching the wall.",
  },
  {
    number: "4",
    title: "Seal Cracks",
    description: "Seal cracks in block or poured concrete with hydraulic cement or epoxy injection.",
  },
  {
    number: "5",
    title: "Install Sump Pump",
    description: "Install a sump pump with battery backup. North Jersey storms and snowmelt can overwhelm a single pump.",
  },
];

const processSteps = [
  {
    icon: Search,
    title: "Inspection",
    description: "We look at the interior and exterior of the foundation, check grading, and identify where water is entering.",
  },
  {
    icon: ClipboardCheck,
    title: "Diagnosis",
    description: "We determine whether the issue is surface drainage, hydrostatic pressure, crack failure, or a combination.",
  },
  {
    icon: FileText,
    title: "Proposal",
    description: "You get a written scope of work with the recommended approach and timeline.",
  },
  {
    icon: Wrench,
    title: "Prep & Installation",
    description: "We handle excavation if needed, membrane application, crack sealing, drain systems, and sump pump installation.",
  },
  {
    icon: Sparkles,
    title: "Cleanup",
    description: "The site is restored and we walk you through what was done and what to watch for going forward.",
  },
];

const commonMistakes = [
  "Skipping the battery backup on the sump pump. When the power goes out during a storm, that's exactly when you need it most.",
  "Ignoring gutter and downspout maintenance. Clogged gutters dump water right next to the foundation.",
  "Finishing the basement before waterproofing it. Drywall, carpet, and framing can hide ongoing moisture problems until the damage is serious.",
];

const faqs = [
  {
    question: "How do I know if my North Jersey basement needs waterproofing?",
    answer: "Look for these five warning signs: cracks in foundation walls, white mineral stains (efflorescence), damp or wet walls after rain, a persistent musty smell, or visible mold. If you notice even one of these, a professional inspection is a good next step. These issues tend to get worse over time, not better.",
  },
  {
    question: "Can I seal my basement walls from the inside?",
    answer: "Interior sealers can reduce vapor diffusion, but they will not stop active water entry on their own. The most common interior solution is a perimeter drain system paired with a sump pump. This approach manages water that enters the basement and redirects it out before it reaches your living space.",
  },
  {
    question: "Do I need a permit for basement waterproofing in North Jersey?",
    answer: "Requirements vary by municipality. Some North Jersey townships require permits for excavation or plumbing work, while others do not. Your contractor should confirm local rules before starting. We handle the permit check as part of our process.",
  },
  {
    question: "Will my homeowner's insurance cover basement waterproofing?",
    answer: "Standard homeowner's policies typically do not cover waterproofing. Most policies cover sudden water damage, like a burst pipe, but not gradual seepage or groundwater intrusion. It's worth reviewing your policy and asking your agent about flood insurance if you're in a low-lying area.",
  },
  {
    question: "How long does a basement waterproofing system last?",
    answer: "Exterior membrane systems can last the life of the home when installed to ASTM standards. Interior drain and sump systems typically last 10 to 25 or more years with regular maintenance. Battery backups on sump pumps should be tested yearly to make sure they're ready when you need them.",
  },
];

const citations = [
  {
    id: 1,
    text: "U.S. EPA. \"A Brief Guide to Mold, Moisture, and Your Home\" – Mold can start growing on wet surfaces within 24 to 48 hours.",
    url: "https://www.epa.gov/mold/brief-guide-mold-moisture-and-your-home",
  },
  {
    id: 2,
    text: "University of Minnesota Extension. \"Moisture in Basements: Causes and Solutions\" – Exterior drainage identified as the most effective long-term approach.",
    url: "https://extension.umn.edu/moisture-and-mold-indoors/moisture-basements-causes-and-solutions",
  },
  {
    id: 3,
    text: "U.S. Department of Energy. \"Moisture Control\" – Air movement carries over 98% of moisture into building cavities.",
    url: "https://www.energy.gov/energysaver/moisture-control",
  },
  {
    id: 4,
    text: "ASTM International. \"Below-Grade Waterproofing Systems\" (D7832) – Performance standards for below-grade waterproofing membranes.",
    url: "https://www.astm.org/d7832-14.html",
  },
  {
    id: 5,
    text: "ICC. IRC 2021, Section R406: Foundation Waterproofing and Dampproofing – Code distinction between dampproofing and waterproofing.",
    url: "https://up.codes/s/foundation-waterproofing-and-dampproofing",
  },
];

export default function BasementWaterproofing() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Basement Waterproofing Totowa, NJ | Masonry Pro Construction</title>
        <meta property="og:description" content="Wet basement in North Jersey? We fix seepage, seal cracks, and install drainage systems. Masonry contractors who know block, stone, and concrete foundations." />
        <meta property="og:title" content="Basement Waterproofing Totowa, NJ | Masonry Pro Construction" />
        <meta
          name="description"
          content="Wet basement in North Jersey? We fix seepage, seal cracks, and install drainage systems. Masonry contractors who know block, stone, and concrete foundations."
        />
        
        {/* FAQPage schema managed by SSG build script — do not duplicate here */}
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Basement Waterproofing",
            description: "Basement waterproofing in North Jersey. Seepage repair, crack sealing, and drainage system installation.",
            url: "/waterproofing-services/basement-waterproofing",
            image: "https://masonry-pro.com/og/basement-waterproofing.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/basement-waterproofing.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/basement-waterproofing.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Basement waterproofing installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <img
                src={heroImage}
                alt="Basement waterproofing membrane and drainage installation along a foundation wall in Ridgewood, Bergen County, NJ"
                width={1200}
                height={800}
                className="sr-only"
                aria-hidden="true"
              />
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Basement Waterproofing in Totowa, NJ Serving{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>North Jersey</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Heavy rain, snowmelt, and clay-heavy soil push water against basement walls year-round. We start with an assessment, put together a repair plan, and schedule the work.
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395">Get a Free Basement Assessment</a>
                </Button>
              </div>
            </div>
          </section>

          {/* How to Waterproof Steps */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                  How Do You Waterproof a Basement in{" "}
                  <span className="text-gradient-gold">North Jersey?</span>
                </h2>
                <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
                  Here are five steps that cover most basement waterproofing projects in this area.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                  {waterproofingSteps.map((step) => (
                    <div key={step.number} className="bg-background rounded-xl p-6 border border-border text-center">
                      <div className="w-12 h-12 rounded-full bg-gradient-gold flex items-center justify-center mx-auto mb-4">
                        <span className="text-secondary-foreground font-bold text-lg">{step.number}</span>
                      </div>
                      <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Warning Signs Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Water in Your Basement Signals a Problem{" "}
                    <span className="text-gradient-gold">Worth Fixing</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      A damp basement is easy to ignore. Maybe there's a musty smell you've gotten used to. Or white, powdery stains on the walls (called efflorescence) that form when water moves through concrete or block and leaves mineral deposits behind. It's one of the earliest signs that moisture is getting in.
                    </p>
                    <p>
                      Other warning signs include hairline cracks in foundation walls, peeling paint, damp spots that come and go after rain, or standing water on the floor.{" "}
                      <a href="https://www.epa.gov/mold/brief-guide-mold-moisture-and-your-home" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                        The EPA notes that mold can start growing on wet surfaces within 24 to 48 hours
                      </a>
                      <sup className="text-secondary">[1]</sup>.
                    </p>
                    <p>
                      In areas like <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties, freeze-thaw cycles speed up concrete cracking. And the clay-heavy soil common across this region holds water against your foundation longer than sandy or loamy soil would.
                    </p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl border border-border">
                  <img
                    src={basementInspection}
                    alt="Basement waterproofing inspection with water damage and efflorescence on cinder block walls in Paramus, Bergen County, NJ."
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Interior vs Exterior Methods */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto items-center">
                <div className="order-2 md:order-1 overflow-hidden rounded-xl border border-border">
                  <img
                    src={basementWallington}
                    alt="Basement waterproofing system with drainage and gravel along a foundation in Wallington, Bergen County, NJ."
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>

                <div className="order-1 md:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Interior and Exterior Methods{" "}
                    <span className="text-gradient-gold">Stop Water at the Source</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      <strong>Interior methods</strong> work well when water is already entering the basement. The most common setup is an interior French drain system along the perimeter of the floor, paired with a sump pump.
                    </p>
                    <p>
                      <strong>Exterior methods</strong> stop water before it gets to the wall. This usually involves excavating along the foundation, applying a waterproof membrane to the exterior surface, and installing drain tile at the footing level.{" "}
                      <a href="https://extension.umn.edu/moisture-and-mold-indoors/moisture-basements-causes-and-solutions" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                        The University of Minnesota Extension identifies exterior drainage as the most effective long-term approach
                      </a>
                      <sup className="text-secondary">[2]</sup>.
                    </p>
                    <p>
                      <strong>Important distinction:</strong> dampproofing and waterproofing are not the same. Many older homes in towns like Montclair, Ridgewood, and Morristown were built with dampproofing that has broken down over the decades. The{" "}
                      <a href="https://up.codes/s/foundation-waterproofing-and-dampproofing" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                        IRC (Section R406)
                      </a>
                      <sup className="text-secondary">[5]</sup> draws a clear line between the two.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* DIY Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                      DIY Patches <span className="text-gradient-gold">Rarely Solve the Real Issue</span>
                    </h2>
                    <div className="space-y-4 text-lg text-muted-foreground">
                      <p>
                        Products like Drylok and Flex Seal are marketed as basement solutions. And they can reduce minor surface dampness for a while. But they are not waterproofing. Those products sit on the surface, so they create a barrier against vapor, but they cannot resist hydrostatic pressure.
                      </p>
                      <p>
                        The{" "}
                        <a href="https://www.energy.gov/energysaver/moisture-control" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                          U.S. Department of Energy explains that air movement carries over 98% of moisture into building cavities
                        </a>
                        <sup className="text-secondary">[3]</sup>. That means the real issue is usually not the wall surface itself.
                      </p>
                      <p>
                        In low-lying parts of North Jersey near the Passaic River and Hackensack River, the water table sits higher than average. No paint-on product is built to handle that kind of force. If you've already tried a DIY fix and the problem came back, you are not alone. It's one of the most common calls we get.
                      </p>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-border">
                    <img
                      src={basementParamus}
                      alt="Basement waterproofing in progress with gravel and waterproof sheeting around a foundation in Paramus, Bergen County, NJ."
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width={800}
                      height={600}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Our Process */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                  A Masonry Contractor Handles the{" "}
                  <span className="text-gradient-gold">Full Scope of Work</span>
                </h2>
                <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
                  Basement waterproofing involves concrete repair, block repair, drainage work, and sometimes excavation. Here's what a typical job looks like with us.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                  {processSteps.map((step) => (
                    <div key={step.title} className="bg-background rounded-xl p-6 border border-border text-center">
                      <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center mx-auto mb-4">
                        <step.icon className="h-6 w-6 text-secondary" />
                      </div>
                      <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                    </div>
                  ))}
                </div>


                <p className="text-muted-foreground text-center mt-6 max-w-3xl mx-auto">
                  <a href="https://www.astm.org/d7832-14.html" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                    ASTM International maintains performance standards (D7832)
                  </a>
                  <sup className="text-secondary">[4]</sup> specifically for below-grade waterproofing membranes. We follow those standards because they exist for a reason.
                </p>
              </div>
            </div>
          </section>

          {/* Long-Term Protection */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Proper Waterproofing Protects Your Foundation{" "}
                  <span className="text-gradient-gold">for Decades</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground mb-8">
                  <p>
                    A waterproofing system is a long-term investment. Exterior membrane systems, when installed correctly, can last the life of the building. Interior drain and sump systems typically last 10 to 25 or more years with proper maintenance.
                  </p>
                  <p>
                    Sump pumps do need attention. Battery backups should be tested at least once a year. In neighborhoods like Wayne, Nutley, and Bloomfield, where spring flooding is a regular event, that yearly check can make a real difference.
                  </p>
                </div>

                <h3 className="text-xl font-bold mb-4">Common Mistakes to Avoid</h3>
                <div className="space-y-3">
                  {commonMistakes.map((mistake) => (
                    <div key={mistake} className="flex items-start gap-3 bg-card rounded-xl p-4 border border-border">
                      <AlertTriangle className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{mistake}</span>
                    </div>
                  ))}
                </div>

                <p className="text-lg text-muted-foreground mt-6">
                  If you're planning to finish your basement or already have, it's worth making sure the waterproofing is solid first. It's a lot easier to address before the walls go up.
                </p>
              </div>
            </div>
          </section>

          {/* Hub Backlink */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-lg text-muted-foreground">
                <p>
                  Basement waterproofing is one part of our <Link to="/waterproofing-services" className="text-secondary hover:underline">waterproofing services in North Jersey</Link>, which also covers patios and foundations.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {faqs.map((faq) => (
                  <div key={faq.question} className="bg-background rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Citations */}
          <section className="py-12">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-sm text-muted-foreground hover:text-foreground">
                      Sources & Citations
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                        {citations.map((citation) => (
                          <li key={citation.id}>{citation.text}</li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="waterproofing" />

          <CTASection serviceName="Basement Waterproofing" />
        </main>

        <Footer />
        <AccessibilityWidget />
      </div>
    </>
  );
}
